import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useRouter from '../../../utils/useRouter';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import { Box } from '@mui/material';
import {
  palette,
  spaces,
} from '../../../assets/styles/themes/tokens';
import { SearchPageContainer } from '../../SearchPage/SearchPageContainer';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { SearchFiltersContainer } from '../../../components/Search/SearchFiltersContainer';
import { SearchFilters, SearchResultsTab } from '../../../types/search';
import queryToSearchFilters from '../../../utils/queryToSearchFilters';
import SearchResults, { SearchResultsItemsTab } from '../../../components/Search/SearchResults/SearchResults';
import {
  searchCourseMindItems as searchCourseMindItemsAction,
  SearchOptions,
} from '../../../actions/search.actions';
import useAction from '../../../utils/useAction';
import {
  getPaginationSelector,
  selectCourseSearchFilters,
} from '../../../reducers/search.selectors';
import services from '../../../services';
import { trackingEvents } from '../../../services/tracking/trackConfig';
import _ from 'lodash';
import useScreenSize from '../../../utils/useScreenSize';
import useCardDimensions from '../../../utils/useCardDimensions';
import { MasterLayoutContext } from '../../../state/MasterLayoutProvider';
import { SearchEventData } from '../../../utils/ampli';
import { zIndex } from '../../../constants';
import NotificationAlert from '../../../components/NotificationAlert';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { Alert } from '../../../components/NotificationAlert/NotificationAlert';

export const useNewUiSearchPagePadding = () => {
  const { isXsScreen, isSmScreen } = useScreenSize();

  if (isXsScreen) return spaces.md;
  if (isSmScreen) return spaces.lg;
  return spaces.xl;
};

const CourseSearchPage = () => {
  const { t } = useTranslation();
  const { match } = useRouter();
  const cardDimensions = useCardDimensions();
  const { mainContentContainer } = useContext(MasterLayoutContext);

  // Computed at render time (not held in a ref reassigned inside the search effect below)
  // so the value passed to SearchResults already describes the *new* search on the render
  // that follows a URL change. SearchResults' SearchCompleted guard compares this against
  // the filters the store's pages were fetched for; a ref would still hold the previous
  // search's filters when that child effect commits, so the two would agree and the event
  // would report the previous search's total under the new search id (PRO-91).
  const requestFilters = useMemo(
    () => queryToSearchFilters(match.location.search),
    [match.location.search],
  );
  const searchCourseMindItems = useAction(searchCourseMindItemsAction);
  const courseMindItemsPagination = useSelector(getPaginationSelector('courseMindItems'));
  const lastCourseSearchFilters = useSelector(selectCourseSearchFilters);
  const { notification, hideNotification, showNotification } = useContext(NotificationContext);

  // mainContentContainer transitions null→ref once on mount (via MasterLayout's
  // ref callback). Reading it through a ref keeps the scroll-to-top behavior
  // intact without making the search effect re-run on that transition — that
  // re-run would otherwise fire a second identical /v4/mindItems/search.
  const mainContentContainerRef = useRef(mainContentContainer);
  mainContentContainerRef.current = mainContentContainer;

  const haveSearchFiltersChanged = (filters?: SearchFilters) => {
    return !_.isEqual(filters, requestFilters);
  };

  const executeCourseMindItemsSearch = (options: SearchOptions) => {
    searchCourseMindItems(
      requestFilters,
      { ...options, populateProducts: true },
    );
    services.track.eventTrack(trackingEvents.searchExecute,_.merge(eventPayload));
  };

  useEffect(() => {
    const options: SearchOptions = {
      clearPreviousResults: true,
    };

    if (haveSearchFiltersChanged(lastCourseSearchFilters)) {
      mainContentContainerRef.current?.scrollTo(0, 0);
      executeCourseMindItemsSearch(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.location.search]);

  const eventPayload: { other: SearchEventData } = {
    other: {
      tab: SearchResultsTab.COURSES,
      results: courseMindItemsPagination.offset,
      query: match.location.query,
      term: requestFilters.term,
      appSection: 'courses',
    },
  };

  const tabs: SearchResultsItemsTab[] = [
    {
      key: SearchResultsTab.COURSES,
      name: t('Courses'),
      icon: 'book',
      loadMore: () => executeCourseMindItemsSearch({ clearPreviousResults: false })
    },
  ];

  const containerSx = {
    maxWidth: cardDimensions.desktop.maxWidth,
    marginX: 'auto',
    paddingX: useNewUiSearchPagePadding(),
    width: '100%',
  };

  const handleShowNotification = (alert: Alert) => {
    showNotification(alert);
  };

  const memoizedNotification = useMemo(() => {
    if (!notification) return null;
    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: zIndex.alert, width: '100%' }}>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <NotificationAlert
            message={notification.message}
            severity={notification.severity}
            onClose={hideNotification}
            alertSx={{
              width: '100%',
              marginLeft: `${spaces.md} !important`,
              marginRight: `${spaces.md} !important`,
              maxWidth: cardDimensions.desktop.maxWidth,
              marginX: 'auto',
            }}
          />
        </Box>
      </Box>
    );
  }, [notification, hideNotification]);

  return <MasterLayout forceNewNavigation>
    <SearchPageContainer>
      {memoizedNotification}
      <Box sx={containerSx}>
        <PageHeader
          sx={{ color: palette.text.primary }}
          text={t('Find your course')}
        />
        <SearchFiltersContainer
          requestFilters={requestFilters}
          visibleKeys={['courseTheme', 'schoolType', 'courseType', 'publisher']}
          invisibleKeys={['courseDuration', 'subject', 'classYear']}
          dialogFiltersType='course'
          dialogFiltersOrder={[
            'courseTheme',
            'schoolType',
            'courseType',
            'publisher',
            'courseDuration',
            'subject',
            'classYear'
          ]}
        />
      </Box>
      <Box marginTop='-24px'>
        <SearchResults
          trackingPayload={eventPayload.other}
          requestFilters={requestFilters}
          tabs={tabs}
          currentTab={SearchResultsTab.COURSES}
          searchTerm={requestFilters.term}
          forceNewDesign
          onShowNotification={handleShowNotification}
        />
      </Box>
    </SearchPageContainer>
  </MasterLayout>;
};

export default CourseSearchPage;
